// ============================================================
// DERIVED VALUES - turning a measurement into the two numbers the
// combat engine already consumes: a `Cover` record and a range band.
//
// IT NO LONGER WRITES ANYTHING BY ITSELF. Cover from an attacker was
// never a property of the defender - it is a relation between two tokens
// and the geometry between them, and two attackers on opposite sides of
// a barricade face different answers about the same person. Storing it
// on `state.cover` made that one shared value, so the second derivation
// silently replaced the first.
//
// So the live answer is a PROJECTION (`projectDefenderFrom`) computed per
// attacker and never persisted, and this module is only the conversion.
// What remains here writes `source: 'manual'`, because the one caller
// left is a person deliberately PINNING a value - and a person's ruling
// outranks the map until they clear it.
//
// The stamp-and-expire machinery this file used to carry
// (`isDerived`, `clearDerivedCover`, `clearDerivedCoverAll`) is gone with
// the storage it existed to clean up.
//
// Pure. Zero React dependency.
// ============================================================
import type { Cover } from '../../types';
import type { Measurement, RangeBand } from './measure';

export interface DerivedValues {
  cover: Cover;
  rangeBand: RangeBand | null;
}

/** The two values a measurement yields, ready for the engine. */
export function measuredValues(measurement: Measurement): DerivedValues {
  return {
    cover: { level: measurement.cover.level, ap: measurement.cover.ap },
    rangeBand: measurement.rangeBand,
  };
}

/**
 * The same values, as a PIN - a deliberate ruling that outranks the map
 * and stays until a person clears it.
 */
export function pinnedValues(measurement: Measurement): DerivedValues {
  const { cover, rangeBand } = measuredValues(measurement);
  return { cover: { ...cover, source: 'manual' }, rangeBand };
}
